/**
 * Platform REST calls for the company portal.
 *
 * The portal's data has historically come over GraphQL; the client
 * dashboard is REST end to end so it ports to the React Query surface in
 * `web-app-next` unchanged. Same bearer as every other Platform call —
 * the in-memory access token minted from the HttpOnly refresh cookie.
 *
 * Bearer only, never `credentials: "include"`. Platform's legacy
 * `wone_project.cors.CorsMiddleware` stamps a wildcard `Access-Control-
 * Allow-Origin` on every path outside `/o/`, `/accounts/` and
 * `/api/v1/auth/`, and a browser rejects a wildcard ACAO on a credentialed
 * cross-origin request — so sending the cookie makes every call here fail
 * at the preflight, in every environment.
 */
import {
  getFreshAccessToken,
  refreshAccessToken,
  PLATFORM_ORIGIN,
} from "providers/auth-tokens";

export class PlatformRestError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "PlatformRestError";
    this.status = status;
  }
}

async function platformFetch(
  path: string,
  method: "GET" | "PUT" = "GET",
  body?: unknown
): Promise<Response> {
  const send = (token: string) =>
    fetch(`${PLATFORM_ORIGIN}${path}`, {
      method,
      headers:
        body === undefined
          ? { Authorization: `Bearer ${token}` }
          : {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

  const token = await getFreshAccessToken();
  if (!token) {
    throw new PlatformRestError(`No access token for ${method} ${path}`, 401);
  }
  let response = await send(token);

  // A token rotated server-side between mint and use answers 401 once. Apollo
  // and coachFetch both refresh and retry there; without the same step this
  // surface is the only one that gives up on a recoverable session.
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      throw new PlatformRestError(`${method} ${path} unauthorized`, 401);
    }
    response = await send(refreshed);
  }

  if (!response.ok) {
    throw new PlatformRestError(
      `${method} ${path} failed: ${response.status}`,
      response.status
    );
  }
  return response;
}

export async function platformGet<T>(path: string): Promise<T> {
  return (await (await platformFetch(path)).json()) as T;
}

export async function platformPut<T>(path: string, body: unknown): Promise<T> {
  return (await (await platformFetch(path, "PUT", body)).json()) as T;
}

export async function platformGetBlob(path: string): Promise<Blob> {
  return (await platformFetch(path)).blob();
}
